import React, { useState, useMemo, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { FiClock, FiTrendingUp } from 'react-icons/fi'
import { Button } from 'react-bootstrap'
import axios from 'axios'
import {
  Meta, Item, Header, Left, PairBadge,
  Right, Chevron, Panel, Rate
} from './TradeViewIntergrationContainer.styles'
import BopCodeComponent from './components/BopCodeComponent'
import AuthContext from "../../../../../auth/auth-context";
import TradeContext from '../../../../../store/tradeState/tradeContext';


function InvestectTradeComponent(props) {
  const { inTrade } = props;

  const data = (inTrade && Array.isArray(inTrade.quotes) && inTrade.quotes.length)
    ? inTrade.quotes
    : [];

  const [quotes, setQuotes] = useState([])
  const [quoteError, setQuoteError] = useState(false)
  const [quoteErrorMessage, setQuoteErrorMessage] = useState('')

  const [trade, setTrade] = useState(null)
  const [tradeError, setTradeError] = useState(false)
  const [tradeErrorMessage, setTradeErrorMessage] = useState('')
  const [tradeOpen, setTradeOpen] = useState(true)
  const [tradeSuccess, setTradeSuccess] = useState(false)
  const [tradeSuccessMessage, setTradeSuccessMessage] = useState('')

  const [bopScreenOpen, setBopScreenOpen] = useState(false)
  const [selectedBopCode, setSelectedBopCode] = useState(null)
  const [bopCodes, setBopCodes] = useState([])

  const [openMap, setOpenMap] = useState({})
  const [loading, setLoading] = useState(false)
  const [tradeLoading, setTradeLoading] = useState(false)
  const [rateConfirmation, setRateConfirmation] = useState({})

  const authCtx = useContext(AuthContext);
  const tradeContext = useContext(TradeContext)

  const headers = {
    "Acting-as": "dealer",
    Authorization: `JWT ${authCtx?.token}`,
  };

  const isSaResident = false // This will need to change to read it from the customer object

  useEffect(() => {
    async function fetchTradeQuotes() {
      try {
        const body = {
          associated_trade_id: props.inTrade.id
        }
        const quotes = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/investec/fx/quote/by-trade-id/`,
          { headers, params: body }
        )
        const formattedQuotes = quotes.data.map((quote) => formatQuote(quote))
        setQuotes(formattedQuotes)
      } catch (error) {
        console.log('Error fetching trade quotes', error)
      }
    }

    async function fetchSubmittedTrades() {
      try {
        const body = {
          associated_local_trade_id: props.inTrade.id
        }
        const trades = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/investec/fx/trade/by-trade-id/`,
          { headers, params: body }
        )
        if (trades.data.length === 0) return
        setTrade(formatTrade(trades.data[0]))
        setBopScreenOpen(true)
      } catch (error) {
        console.log('Error fetching submitted trades', error)
      }
    }

    if (props.inTrade.id) {
      fetchTradeQuotes()
      fetchSubmittedTrades()
    }
  }, [props.inTrade])

  useEffect(() => {
    async function fetchInvestecBopCodes() {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/investec/fx/bop/codes/?is_sa_resident=${isSaResident}`,
          { headers: headers }
        )
        const formatted = formatBopCodes(response.data)
        setBopCodes(formatted)
      } catch (error) {
        console.log('Error fetching bop codes', error)
      }
    }

    fetchInvestecBopCodes()
  }, [])

  const tradeAllowed = useMemo(() => {
      const status = props?.inTrade?.status;
      
      if(status === 'CANCELLED' || status === 'COMPLETED' || status === 'BOOKED'){
        return false;
      }
      return true;
    }, [props?.inTrade?.status]);

    const sortedQuotes = useMemo(() => {
      const safeDate = (v) => {
        const d = new Date(v)
        return isNaN(d.getTime()) ? 0 : d.getTime()
      }
      return [...quotes].sort((a, b) => safeDate(b.createdAt) - safeDate(a.createdAt))
    }, [quotes])

  const newestSeenRef = React.useRef(null)
    useEffect(() => {
      if (sortedQuotes.length === 0) return
      const newestId = sortedQuotes[0].id
      if (newestSeenRef.current === null || newestSeenRef.current !== newestId) {
        newestSeenRef.current = newestId
        setOpenMap((prev) => ({ ...prev, [newestId]: true }))
      }
    }, [sortedQuotes])

  

  const hasTrade = Boolean(trade && Object.keys(trade).length > 0)

  async function generateInvestecQuote() {
    setQuoteError(false)
    setTradeErrorMessage('')
    setTradeError(false)
    setQuoteErrorMessage('')
    setLoading(true)

    try {
      if (props?.inTrade?.status === 'CANCELLED') {
        setLoading(false)
        setQuoteError(true)
        setQuoteErrorMessage('Cannot generate quote for a cancelled trade.')
        return
      }
      const body = {
        associated_local_trade_id: props?.inTrade?.id
      }
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/investec/fx/quote/`,
        body,
        { headers: headers }
      )

      setQuotes((prevQuotes) => [
        ...prevQuotes,
        formatQuote(response.data)
      ])

      setLoading(false)
    } catch (error) {
      console.log('Error generating quote', error)
      setLoading(false)
      setQuoteError(true)
      setQuoteErrorMessage(error.response?.data?.error || 'An error occurred while generating the quote.')
    }
  }

  const handleCompleteTrade = async () => {
      const headers = {
        "Acting-as": "dealer",
        Authorization: `JWT ${authCtx?.token}`,
      };
      try {
        // Make API call to cancel trade
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/trades/${props.inTrade?.id}/set_completed/`,
          {},
          { headers: headers },
        );
        
        // Update trade status in the context state
        if (response.data.status === 'success') {
          tradeContext.updateTrade(props.inTrade?.id, { status: "COMPLETED" });
          tradeContext.setTrade({ ...props.inTrade, status: "COMPLETED" });
        }
        
        // Close the trade view
        // props.close();
      } catch (err) {
        console.error("Error completing trade:", err);
        // setCancelTradeLoading(false);
        // Optionally show error to user
      }
    };

  function extractInvestecErrorMessage(str) {
    const jsonStart = str.indexOf('{')
    if (jsonStart === -1) return str
    try {
      const parsed = JSON.parse(str.slice(jsonStart))
      return parsed.message || parsed.errors?.[0]?.message || str
    } catch {
      const match = str.match(/"message"\s*:\s*"([^"]*)"/)
      return match ? match[1] : str
    }
  }

  async function submitInvestecTrade(quoteId) {
    try {
      if (!quoteId) return
      setTradeErrorMessage('')
      setTradeError(false)
      setQuoteErrorMessage('')
      setQuoteError(false)
      setTradeLoading(true)

      if (props?.inTrade?.status === 'CANCELLED') {
        setTradeLoading(false)
        setQuoteError(true)
        setQuoteErrorMessage('Cannot generate quote for a cancelled trade.')
        return
      }

      if (!rateConfirmation) {
        setTradeLoading(false)
        setTradeErrorMessage('Please confirm the rate approval checkbox before submitting the trade.')
        setTradeError(true)
        return
      }
      const body = {
        quote_id: quoteId
      }
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/investec/fx/trade/`,
        body,
        { headers: headers }
      )
      if (response.data.status === 'Success') {
        console.log('Trade submission response', response.data)
        // await handleCompleteTrade()
        setBopScreenOpen(true)
        setTradeLoading(false)
        setTrade(formatTrade(response.data))
      }
    } catch (error) {
      console.log('Error executing trade', error)
      setTradeLoading(false)
      setTradeError(true)
      setTradeErrorMessage(extractInvestecErrorMessage(error.response?.data?.error) || 'An error occurred while submitting the trade.')
    }
  }

  const formatQuote = (quote) => {
    if (!quote) return {}
    return {
      id: quote.id,
      request_id: quote.request_id,
      rate_id: quote.rate_id,
      createdAt: quote.request_date_time,
      currencyPair: `${quote.sell_currency}/${quote.buy_currency}`,
      client_rate: quote.client_rate,
      max_client_rate: props.inTrade.max_exchange_rate,
      client_rate_with_margin: quote.client_rate_with_margin,
      client_trade_rate: props.inTrade.trade_rate,
      rate: quote.client_rate_with_margin,
      amount: quote.amount,
    }
  }

  const formatTrade = (trade) => {
    if (!trade) return {}
    return {
      id: trade.id,
      associated_local_trade_id: trade.associated_local_trade_id,
      associated_quote_id: trade.associated_quote_id,
      account_number: trade.account_number,
      status: trade.status,
      trade_reference: trade.trade_reference,
      counterparty_client_rate: trade.counterparty_client_rate,
    }
  }

  function formatBopCodes(data) {
    const classMap = new Map()

    for (const item of data) {
      if (!classMap.has(item.category_class)) {
        classMap.set(item.category_class, new Map())
      }
      const categoryMap = classMap.get(item.category_class)

      if (!categoryMap.has(item.category)) {
        categoryMap.set(item.category, [])
      }
      categoryMap.get(item.category).push({
        id: item.id,
        bop_code: item.code,
        sub_code: item.sub_code,
        description: item.description,
      })
    }

    return Array.from(classMap, ([className, categoryMap]) => ({
      name: className,
      items: Array.from(categoryMap, ([categoryName, codes]) => ({
        name: categoryName,
        items: codes,
      })),
    }))
  }

  const formatDate = useMemo(
    () => (iso) => new Date(iso).toLocaleString(),
    []
  )

  const toggle = (id) =>
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }))

  function isExpirationWithinTenMinutes(createdAt) {
    const ts = new Date(createdAt).getTime()
    if (!Number.isFinite(ts)) return false
    const now = Date.now()
    const delta = now - ts
    return delta > (10 * 60 * 1000)
  }

  const handleBopCodeSelect = (item) => {
    console.log('Selected BOP code', item)
    setSelectedBopCode(item)
  }

  const handleSubmitBopCode = async () => {
    console.log('Submitting BOP code', selectedBopCode)
    console.log('selected trade id: ', trade.id)
    try {
        if(!trade?.id) {
            setTradeError(true)
            return setTradeErrorMessage('No associated trade found to submit the BOP code against.')
        }
        if(!selectedBopCode) {
            setTradeError(true)
            return setTradeErrorMessage('Please select a BOP code before submitting.')
        }
        setLoading(true)
        setTradeError(false)
        setTradeErrorMessage('')
        const body = {
            bop_code_id: selectedBopCode.id,
            associated_local_trade_id: inTrade.id
        }
        console.log('Submitting BOP code with body', body)

        const response = await axios.post(
            `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/investec/fx/bop/apply-code/`,
            body,
             { headers: headers }
        )
        console.log('BOP code submission response', response)

        if(response.status === 200) {
            // Optionally, you could refresh the trade details here to reflect the applied BOP code
            setTradeError(false)
            setTradeErrorMessage('')
            setLoading(false)
            setTradeSuccess(true)
            setTradeSuccessMessage(response.data.message || 'BOP code applied successfully.')
            return;
        }
        
    } catch(err){
        console.log('Error submitting BOP code', err)
        setTradeError(true)
        setTradeErrorMessage(err.response?.data?.error || 'An error occurred while submitting the BOP code.')
        setLoading(false)
    }
  }

  return (
    <div>
      {!hasTrade && sortedQuotes.length === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
          <Meta>No quotes available</Meta>
          <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', marginTop: 16 }}>
            <Button disabled={loading} variant="primary" onClick={generateInvestecQuote} style={{ width: "250px", minWidth: "250px", maxWidth: "250px" }}>
              {loading ? 'Generating…' : 'Get Quote'}
            </Button>
            {!hasTrade && tradeAllowed === true && (
              <Button variant="warning" onClick={() => props.setTradeScreen('manual_booking')} style={{ width: "250px", minWidth: "250px", maxWidth: "250px", color: 'white' }}>
                Manual Trade
              </Button>
            )}
          </div>
        </div>
      )}

      {!hasTrade && sortedQuotes.length > 0 && (
        <>
          {sortedQuotes.map((quote) => {
            const isOpen = !!openMap[quote.id]
            const panelId = `quote-panel-${quote.id}`
            const isStale = isExpirationWithinTenMinutes(quote.createdAt)

            return (
              <Item key={quote.id}>
                <Header
                  type="button"
                  onClick={() => toggle(quote.id)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  $open={isOpen}
                >
                  <Left>
                    <PairBadge>
                      {quote.currencyPair}
                    </PairBadge>
                    <Meta title={quote.createdAt} style={{ color: isStale ? '#dc3545' : undefined }}>
                      <FiClock style={{ marginRight: 6 }} /> {formatDate(quote.createdAt)}
                    </Meta>
                  </Left>
                  <Right>
                    <Meta>Amount</Meta>
                    <strong>
                      {quote.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                    <Meta><FiTrendingUp style={{ marginRight: 6 }} /> Created Rate</Meta>
                    <Rate>{quote.client_trade_rate.toFixed(4)}</Rate>
                    <Chevron size={18} $open={isOpen} aria-hidden />
                  </Right>
                </Header>

                <Panel id={panelId} role="region" aria-label="Quote details" $open={isOpen}>
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                    <li style={{ padding: '8px 0', borderBottom: '1px solid #f1f3f5' }}>
                      <Meta>Created</Meta>: {formatDate(quote.createdAt)}
                    </li>
                    <li style={{ padding: '8px 0', borderBottom: '1px solid #f1f3f5' }}>
                      <Meta>Pair</Meta>: <strong>{quote.currencyPair}</strong>
                    </li>
                    <li style={{ padding: '8px 0', borderBottom: '1px solid #f1f3f5' }}>
                      <Meta>Amount</Meta>: <strong>{quote.amount.toLocaleString()}</strong>
                    </li>
                    <li style={{ padding: '8px 0' }}>
                      <Meta>Max Client Rate</Meta>: <strong>{quote.max_client_rate.toFixed(4)}</strong>
                    </li>
                    <li style={{ padding: '8px 0' }}>
                      <Meta>Final Trade Rate</Meta>: <strong>{quote.rate}</strong>
                    </li>
                  </ul>
                  {/* final trade rate > max client rate */}
                  {quote.rate > props.inTrade.trade_rate && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!rateConfirmation[quote.id]}
                        onChange={(e) => setRateConfirmation(prev => ({ ...prev, [quote.id]: e.target.checked }))}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.9rem', color: '#dc3545' }}>
                        I confirm that the final trade rate exceeds the maximum approved trade rate of ({quote.max_client_rate.toFixed(4)}),
                        and the client has explicitly given their approval for us to proceed with the execution of the trade at this rate.
                      </span>
                    </label>
                  )}
                  {!isStale && (
                    <Button
                      disabled={tradeLoading || (quote.rate > props.inTrade.trade_rate && !rateConfirmation[quote.id])}
                      variant="outline-primary"
                      style={{ marginTop: 16 }}
                      onClick={() => submitInvestecTrade(quote.id)}
                    >
                      {tradeLoading ? 'Processing…' : 'Submit Trade'}
                    </Button>
                  )}
                </Panel>
              </Item>
            )
          })}
          <div style={{ display: 'flex', gap: '12px', marginTop: 16 }}>
            {tradeAllowed === true && (
              <Button variant="warning" onClick={() => props.setTradeScreen('manual_booking')} style={{ width: "250px", minWidth: "250px", maxWidth: "250px", color: 'white' }}>
                Manual Trade
              </Button>
            )}
            <Button disabled={loading} variant="primary" onClick={generateInvestecQuote} style={{ width: "250px", minWidth: "250px", maxWidth: "250px" }}>
              {loading ? 'Generating…' : 'Get Quote'}
            </Button>
          </div>
        </>
      )}

      {hasTrade && (
        <Item>
          <Header
            type="button"
            onClick={() => setTradeOpen((v) => !v)}
            aria-expanded={tradeOpen}
            aria-controls="submitted-trade-panel"
            $open={tradeOpen}
          >
            <Left>
              <PairBadge>Submitted Trade</PairBadge>
              <Meta title={`Trade ID ${trade.id || ''}`}>
                Ref: <strong style={{ marginLeft: 6 }}>{trade.trade_reference || '—'}</strong>
              </Meta>
            </Left>
            <Right>
              <Meta>Status</Meta>
              <strong>{trade.status || '—'}</strong>
              <Meta><FiTrendingUp style={{ marginRight: 6 }} /> Trade Rate</Meta>
              <Rate>
                {Number.isFinite(Number(trade.counterparty_client_rate))
                  ? Number(trade.counterparty_client_rate).toFixed(4)
                  : '—'}
              </Rate>
              <Chevron size={18} $open={tradeOpen} aria-hidden />
            </Right>
          </Header>

          <Panel id="submitted-trade-panel" role="region" aria-label="Submitted trade details" $open={tradeOpen}>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #f1f3f5' }}>
                <Meta>Account Number</Meta>: <strong>{trade.account_number ?? '—'}</strong>
              </li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #f1f3f5' }}>
                <Meta>Status</Meta>: <strong>{trade.status ?? '—'}</strong>
              </li>
              <li style={{ padding: '8px 0' }}>
                <Meta>Executed Trade Rate</Meta>: <strong>
                  {Number.isFinite(Number(trade.counterparty_client_rate))
                    ? Number(trade.counterparty_client_rate).toFixed(4)
                    : '—'}
                </strong>
              </li>
            </ul>
          </Panel>
        </Item>
      )}

      {quoteError && (
        <div style={{ marginTop: 12, color: 'red' }}>
          {quoteErrorMessage || 'An error occurred while generating the quote.'}
        </div>
      )}

      {tradeError && (
        <div style={{ marginTop: 12, color: 'red' }}>
          {tradeErrorMessage || 'An error occurred while submitting the trade.'}
        </div>
      )}
      {tradeSuccess && (
        <div style={{ marginTop: 12, color: 'green' }}>
          {tradeSuccessMessage || 'Trade submitted successfully.'}
        </div>
      )}

      {bopScreenOpen && (
        <>
            <BopCodeComponent
            onSelect={handleBopCodeSelect}
            selectedId={selectedBopCode?.id}
            data={bopCodes}
            />
            <Button style={{ marginTop: 16 }} onClick={handleSubmitBopCode} disabled={loading}>
              {loading ? 'Submitting…' : 'Submit'}
            </Button>
        </>
      )}
    </div>
  )
}

export default InvestectTradeComponent

InvestectTradeComponent.propTypes = {
  inTrade: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    max_exchange_rate: PropTypes.number,
    trade_rate: PropTypes.number,
    status: PropTypes.string,
    quotes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        createdAt: PropTypes.string,
        currencyPair: PropTypes.string,
        amount: PropTypes.number,
        rate: PropTypes.number,
      })
    ),
  }),
  setTradeScreen: PropTypes.func.isRequired,
}